/**
 * CoinFloater — render danh sách floating coin "+X ☕" bay từ vị trí
 * customer tới HUD góc trên phải.
 *
 * Animation: framer-motion (fade in → bay → fade out, 1.0–1.2s).
 * Tham chiếu:
 *   - `docs/analysis/02_use_cases.md` UC-06 §7.2 step 9.2.
 *   - design/7. Thiết kế RevenueTicker.png.
 */
import { AnimatePresence, motion } from "framer-motion";

import type { FloatingCoin } from "../types";

interface Props {
  coins: FloatingCoin[];
  /** Vị trí target (HUD); fallback góc trên phải. */
  targetX?: number;
  targetY?: number;
}

const ANIMATION_DURATION = 1.1; // 1.1s — bám design RevenueTicker.png

export function CoinFloater({ coins, targetX, targetY = 50 }: Props) {
  // Mặc định bay tới góc trên phải nếu không truyền target.
  const resolvedTargetX =
    targetX ?? (typeof window !== "undefined" ? window.innerWidth - 100 : 300);

  return (
    <AnimatePresence>
      {coins.map((coin) => (
        <motion.div
          key={coin.id}
          data-testid="coin-floater"
          data-coin-type={coin.type}
          initial={{
            x: coin.startX,
            y: coin.startY,
            opacity: 0,
            scale: 0.5,
          }}
          animate={{
            x: resolvedTargetX,
            y: targetY,
            opacity: [0, 1, 1, 0],
            scale: [0.5, 1.2, 1, 0.8],
          }}
          exit={{ opacity: 0 }}
          transition={{
            duration: ANIMATION_DURATION,
            ease: "easeOut",
            times: [0, 0.2, 0.8, 1],
          }}
          className={`fixed pointer-events-none font-bold text-2xl z-50 ${
            coin.type === "tip" ? "text-amber50" : "text-coffee"
          }`}
        >
          +{coin.amount} {coin.type === "tip" ? "💰" : "☕"}
        </motion.div>
      ))}
    </AnimatePresence>
  );
}
